import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import markdownToHtml from "../lib/markdown/markdownToHtml";
import { getLocale } from "../redux/modules/translations/selector";
import {
  STOCK_AI_ANALYSIS_POLL_INTERVAL_MS,
  STOCK_AI_ANALYSIS_POLL_TIMEOUT_MS,
  STOCK_AI_ANALYSIS_STATUS,
  STOCK_AI_ANALYSIS_VIEW
} from "../lib/stockAiAnalysis/constants";
import useGetRequest from "./useGetRequest";

const STOCK_AI_ANALYSIS_URL_PATH = "app.stock.aiAnalysis";
const STOCK_AI_ANALYSIS_RETRY_URL_PATH = "app.stock.aiAnalysisRetry";

const getErrorStatus = error =>
  error?.response?.status ?? error?.request?.status;

const resolveStockAiAnalysisView = ({
  isNotAvailable,
  isTimedOut,
  error,
  status,
  loading,
  isPolling,
  isRetrying,
  html
}) => {
  if (isNotAvailable) {
    return STOCK_AI_ANALYSIS_VIEW.NOT_AVAILABLE;
  }

  if (isTimedOut || (error && !isNotAvailable && !isRetrying)) {
    return STOCK_AI_ANALYSIS_VIEW.ERROR;
  }

  if (status === STOCK_AI_ANALYSIS_STATUS.FAILED && !isRetrying) {
    return STOCK_AI_ANALYSIS_VIEW.ERROR;
  }

  if (
    isRetrying ||
    status === STOCK_AI_ANALYSIS_STATUS.REQUESTED ||
    ((loading || isPolling) && !html)
  ) {
    return STOCK_AI_ANALYSIS_VIEW.LOADING;
  }

  if (
    (status === STOCK_AI_ANALYSIS_STATUS.READY ||
      status === STOCK_AI_ANALYSIS_STATUS.OUTDATED) &&
    html
  ) {
    return STOCK_AI_ANALYSIS_VIEW.CONTENT;
  }

  if (html) {
    return STOCK_AI_ANALYSIS_VIEW.CONTENT;
  }

  if (loading || isPolling) {
    return STOCK_AI_ANALYSIS_VIEW.LOADING;
  }

  return STOCK_AI_ANALYSIS_VIEW.ERROR;
};

const useStockAiAnalysis = ({ market, ticker, type }) => {
  const locale = useSelector(getLocale);
  const pollingStartedAtRef = useRef(null);
  const skipRetryClearRef = useRef(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [useRetryUrl, setUseRetryUrl] = useState(false);

  const urlPath = !market || !ticker || !type
    ? null
    : useRetryUrl
      ? STOCK_AI_ANALYSIS_RETRY_URL_PATH
      : STOCK_AI_ANALYSIS_URL_PATH;

  const { response, error, loading, refetch } = useGetRequest({
    urlPath,
    variables: { market, ticker, type },
    locale,
    skipAccessCheck: true,
    clearResponseOnVariablesChange: true
  });

  useEffect(() => {
    pollingStartedAtRef.current = null;
    setIsPolling(false);
    setIsTimedOut(false);
    setUseRetryUrl(false);
    skipRetryClearRef.current = false;
  }, [market, ticker, type, locale]);

  useEffect(() => {
    if (!useRetryUrl) {
      return;
    }

    if (skipRetryClearRef.current) {
      skipRetryClearRef.current = false;
      return;
    }

    setUseRetryUrl(false);
  }, [useRetryUrl, response, error]);

  const status = response?.status ?? null;
  const content = response?.content ?? null;
  const createdAt = response?.created_at ?? null;
  const warning = response?.warning ?? null;
  const isNotAvailable = getErrorStatus(error) === 404;
  const canRetry = status === STOCK_AI_ANALYSIS_STATUS.FAILED;

  const html = useMemo(
    () => (content ? markdownToHtml(content) : null),
    [content]
  );

  useEffect(() => {
    if (
      isNotAvailable ||
      isTimedOut ||
      (error && !isNotAvailable) ||
      status !== STOCK_AI_ANALYSIS_STATUS.REQUESTED
    ) {
      if (status !== STOCK_AI_ANALYSIS_STATUS.REQUESTED) {
        pollingStartedAtRef.current = null;
        setIsPolling(false);
      }

      return undefined;
    }

    if (!pollingStartedAtRef.current) {
      pollingStartedAtRef.current = Date.now();
    }

    setIsPolling(true);

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - pollingStartedAtRef.current;

      if (elapsed >= STOCK_AI_ANALYSIS_POLL_TIMEOUT_MS) {
        setIsTimedOut(true);
        setIsPolling(false);
        clearInterval(intervalId);
        return;
      }

      refetch();
    }, STOCK_AI_ANALYSIS_POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [error, isNotAvailable, isTimedOut, refetch, status]);

  const retryAnalysis = useCallback(() => {
    pollingStartedAtRef.current = null;
    setIsTimedOut(false);
    setIsPolling(false);
    skipRetryClearRef.current = true;
    setUseRetryUrl(true);
  }, []);

  const view = resolveStockAiAnalysisView({
    isNotAvailable,
    isTimedOut,
    error,
    status,
    loading,
    isPolling,
    isRetrying: useRetryUrl,
    html
  });

  return {
    content,
    html,
    status,
    createdAt,
    warning,
    view,
    loading: view === STOCK_AI_ANALYSIS_VIEW.LOADING,
    isPolling,
    error: isTimedOut ? new Error("stock_ai_analysis_timeout") : error,
    isNotAvailable,
    isTimedOut,
    canRetry,
    retryAnalysis,
    refetch
  };
};

export default useStockAiAnalysis;
